<template>
  <div id="packaging-dashboard" :class="{ 'pd--dark': isDark }">
    <!-- PANEL LATERAL (SIDEBAR) -->
    <DashboardSidebar
      :class="['pd-sidebar', { 
        'pd-sidebar--mobile-open': !isDesktop && sidebarOpen,
        'pd-sidebar--desktop-closed': isDesktop && !sidebarOpen
      }]"
      :dataset="masterDataset"
      :activeTab="activeTab"
      @close-sidebar="sidebarOpen = false"
      @filter-changed="onFilterChanged"
      @tab-changed="onTabChanged"
    />

    <!-- BOTON FLOTANTE TOGGLE SIDEBAR DESKTOP -->
    <button 
      v-if="isDesktop"
      class="pd-sidebar-toggle"
      :class="{ 'pd-sidebar-toggle--closed': !sidebarOpen }"
      @click="sidebarOpen = !sidebarOpen"
    >
      <i class="fa-solid fa-chevron-left"></i>
    </button>

    <!-- Overlay mobile -->
    <div
      v-if="!isDesktop && sidebarOpen"
      class="pd-overlay"
      @click="sidebarOpen = false"
    ></div>

    <!-- CUERPO PRINCIPAL -->
    <main class="pd-main">
      <!-- HEADER -->
      <DashboardHeader
        :title="activeTab === 'resumen'
          ? 'Dashboard de Control de Inventario y Abastecimiento de Empaque y Embalaje'
          : activeTab === 'gestion' 
            ? 'Dashboard Gestión de Compras y Abastecimiento'
            : 'Explorador de Precios Unitarios e Históricos de Adquisición'"
        :subtitle="activeTab === 'resumen'
          ? 'Monitoreo transaccional de órdenes e histórico de precios para materiales de embalaje.'
          : activeTab === 'gestion'
            ? 'Monitoreo de cumplimiento, nivel de servicio de proveedores y proyecciones de ingreso de materiales.'
            : 'Analiza tendencias de compra, variaciones de costo unitario en USD y cotizaciones de tus proveedores.'"
        :themeMode="themeMode"
        :isSyncing="isSyncing"
        :lastSyncDate="lastSyncDate"
        @toggle-sidebar="sidebarOpen = !sidebarOpen"
        @theme-changed="onThemeChanged"
        @sync-data="syncData"
        @export-excel="exportToExcel"
        @ai-audit="runAiAudit"
      />

      <!-- CONTENIDO (Scrollable) -->
      <div class="pd-content">
        
        <!-- ASISTENTE INTELIGENTE GEMINI IA -->
        <div v-if="aiAuditVisible" class="pd-ai-container">
          <div class="pd-ai-header">
            <h3 class="pd-ai-title">
              <i class="fa-solid fa-wand-magic-sparkles fa-beat"></i>
              Estrategia y Auditoría IA - Sunshine Materials
            </h3>
            <div class="pd-ai-actions">
              <button v-if="!aiAuditLoading && aiAuditHtml" @click="copyAiResponse" class="pd-ai-btn" title="Copiar al portapapeles">
                <i class="fa-regular fa-copy"></i> Copiar
              </button>
              <button @click="aiAuditVisible = false" class="pd-ai-btn" title="Cerrar">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>
          
          <div v-if="aiAuditLoading" class="pd-ai-loading">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>Auditando fluctuaciones de precios unitarios y optimización de proveedores...</span>
          </div>
          
          <div v-else class="pd-ai-body" v-html="aiAuditHtml"></div>
        </div>
        <DashboardSkeleton v-if="isLoading" />
        <div v-else style="display: contents;">
          <DashboardSummary v-if="activeTab === 'resumen' && $fun.hasSpecialPermission('viewDashboardSummary')" :dataset="filteredDataset" :orderType="currentFilters ? currentFilters.OrderType : null" :isDark="isDark" />
          <DashboardManagement v-if="activeTab === 'gestion' && $fun.hasSpecialPermission('viewDashboardSummary')" :dataset="filteredDataset" :orderType="currentFilters ? currentFilters.OrderType : null" :pipelineStats="pipelineStats" :isDark="isDark" />
          <DashboardHistory v-if="activeTab === 'historico' && $fun.hasSpecialPermission('viewDashboardHistory')" :dataset="filteredDataset" :selectedSku.sync="selectedSku" :orderType="currentFilters ? currentFilters.OrderType : null" :isDark="isDark" />
          <DashboardReceipt v-if="activeTab === 'ingreso' && $fun.hasSpecialPermission('viewDashboardReceipt')" :dataset="filteredDataset" :orderType="currentFilters ? currentFilters.OrderType : null" :isDark="isDark" />
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import DashboardSidebar from './components/DashboardSidebar.vue';
import DashboardHeader  from './components/DashboardHeader.vue';
import DashboardSummary from './components/DashboardSummary.vue';
import DashboardManagement from './components/DashboardManagement.vue';
import DashboardHistory from './components/DashboardHistory.vue';
import DashboardReceipt from './components/DashboardReceipt.vue';
import DashboardSkeleton from './components/DashboardSkeleton.vue';
import DashboardService from '@/services/Logistics/DashboardService';
import swal from 'sweetalert2';
import ExcelJS from 'exceljs';

export default {
  name: 'PackagingDashboard',
  components: { DashboardSidebar, DashboardHeader, DashboardSummary, DashboardManagement, DashboardHistory, DashboardReceipt, DashboardSkeleton },
  data() {
    return {
      sidebarOpen: window.innerWidth >= 1024,
      isDesktop: window.innerWidth >= 1024,
      activeTab: null,
      themeMode: localStorage.getItem('pd-theme') || 'system',
      systemPrefersDark: window.matchMedia('(prefers-color-scheme: dark)').matches,
      isSyncing: false,
      lastSyncDate: null,
      masterDataset: [],
      filteredDataset: [],
      pipelineStats: null,
      currentFilters: null,
      selectedSku: null,
      isLoading: false,
      
      // Variables para IA
      aiAuditVisible: false,
      aiAuditLoading: false,
      aiAuditHtml: '',
      rawAiText: '', // Texto plano para copiar
    };
  },
  created() {
    if (this.$fun.hasSpecialPermission('viewDashboardSummary')) {
      this.activeTab = 'resumen';
    } else if (this.$fun.hasSpecialPermission('viewDashboardHistory')) {
      this.activeTab = 'historico';
    } else if (this.$fun.hasSpecialPermission('viewDashboardReceipt')) {
      this.activeTab = 'ingreso';
    }
  },
  mounted() {
    this.checkDesktop();
    window.addEventListener('resize', this.checkDesktop);
    this._mq = window.matchMedia('(prefers-color-scheme: dark)');
    this._mqHandler = (e) => { this.systemPrefersDark = e.matches; };
    this._mq.addEventListener('change', this._mqHandler);
    this.loadExternalDeps();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkDesktop);
    if (this._mq) this._mq.removeEventListener('change', this._mqHandler);
  },
  methods: {
    checkDesktop() {
      const wasDesktop = this.isDesktop;
      this.isDesktop = window.innerWidth >= 1024;
      
      // Auto-abrir al pasar de mobile a desktop
      if (!wasDesktop && this.isDesktop) {
        this.sidebarOpen = true;
      } 
      // Auto-cerrar al pasar de desktop a mobile
      else if (wasDesktop && !this.isDesktop) {
        this.sidebarOpen = false;
      }
    },
    onThemeChanged(mode) {
      this.themeMode = mode;
      localStorage.setItem('pd-theme', mode);
    },
    loadExternalDeps() {
      if (!document.getElementById('pd-fa-cdn')) {
        const fa = document.createElement('link');
        fa.id   = 'pd-fa-cdn';
        fa.rel  = 'stylesheet';
        fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
        document.head.appendChild(fa);
      }
      if (!document.getElementById('pd-inter-cdn')) {
        const font = document.createElement('link');
        font.id   = 'pd-inter-cdn';
        font.rel  = 'stylesheet';
        font.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
        document.head.appendChild(font);
      }
      if (!document.getElementById('pd-chartjs-cdn')) {
        const chartjs = document.createElement('script');
        chartjs.id  = 'pd-chartjs-cdn';
        chartjs.src = 'https://cdn.jsdelivr.net/npm/chart.js';
        document.head.appendChild(chartjs);
      }
    },
    async fetchData(filters = null) {
      if (!filters) {
        filters = { 
          year: String(new Date().getFullYear()), 
          month: null, supplier: null, rubro: null, comprador: null, estado: null 
        };
      }
      
      let dateBegin = "20000101";
      let dateEnd = "20991231";
      
      if (filters.year && filters.year !== 'ALL') {
         const years = filters.year.split(',').map(y => parseInt(y, 10)).filter(y => !isNaN(y));
         if (years.length > 0) {
            const minYear = Math.min(...years);
            const maxYear = Math.max(...years);
            
            if (filters.month && filters.month !== 'ALL') {
               const months = filters.month.split(',').map(m => parseInt(m, 10)).filter(m => !isNaN(m));
               if (months.length > 0) {
                  const minMonth = Math.min(...months);
                  const maxMonth = Math.max(...months);
                  dateBegin = `${minYear}${String(minMonth).padStart(2, '0')}01`;
                  const days = new Date(maxYear, maxMonth, 0).getDate();
                  dateEnd = `${maxYear}${String(maxMonth).padStart(2, '0')}${days}`;
               }
            } else {
               dateBegin = `${minYear}0101`;
               dateEnd = `${maxYear}1231`;
            }
         }
      }
      let payloadAreaID = null;
      if (this.$fun.isLevelArea()) {
         payloadAreaID = this.$fun.getUserInfo()?.CtrContract?.AreID ?? null;
      }

      const payload = {
        DateBegin: dateBegin,
        DateEnd: dateEnd,
        Years: filters.year === 'ALL' ? null : filters.year,
        Months: filters.month === 'ALL' ? null : filters.month,
        OrderType: filters.OrderType === 'ALL' ? null : filters.OrderType,
        SupCode: filters.SupCode === 'ALL' ? null : filters.SupCode,
        CategoryCode: filters.CategoryCode === 'ALL' ? null : filters.CategoryCode,
        ShopperID: filters.ShopperID === 'ALL' ? null : filters.ShopperID,
        OrdStatus: filters.OrdStatus === 'ALL' ? null : filters.OrdStatus,
        AreID: payloadAreaID
      };

      this.isLoading = true;
      try {
        const response = await DashboardService.getOrderPurchaseReport(payload);
        const data = response.data || [];
        console.log("datos obtenidos del dashboard: ",data);
        
        // Fetch pipeline stats solo si estamos en gestion
        if (this.activeTab === 'gestion') {
            this.fetchPipelineStats(filters);
        }
        // Usamos el JSON nativo sin traducir propiedades al español
        this.masterDataset = data;
        console.log("datos cargados y listos: ", this.masterDataset);
        // Mantenemos el filtro local en caso el SP aún no filtre todo nativamente
        this.applyLocalFilters(filters);
        
        // Obtener la última fecha de sincronización al cargar la data
        try {
            const syncResponse = await DashboardService.getDashboardLastSync();
            if (syncResponse.data && syncResponse.data.Success) {
                this.lastSyncDate = syncResponse.data.Data;
            }
        } catch (syncErr) {
            console.error("Error obteniendo last sync date:", syncErr);
        }
        
      } catch (error) {
        console.error("Error obteniendo la data del dashboard:", error);
      } finally {
        this.isLoading = false;
      }
    },
    onFilterChanged(filters) {
      // Guardamos los filtros actuales para poder usarlos en recargas reactivas
      this.currentFilters = filters;
      
      this.fetchData(filters);
    },
    async syncData() {
      if (this.isSyncing) return;
      this.isSyncing = true;
      try {
        const response = await DashboardService.syncDashboardData();
        if (response.data && response.data.Success) {
          this.lastSyncDate = response.data.Data; // Ej: "18/06/2026 15:30:00"
          this.$fun.alert(response.data.Message, 'success');
          // Actualizar las graficas volviendo a pedir la data de forma reactiva
          if (this.masterDataset.length > 0) {
             this.fetchData(this.currentFilters);
          }
        } else {
          this.$fun.alert(response.data?.Message || 'Error desconocido al sincronizar', 'error');
        }
      } catch (error) {
        console.error('Error sincronizando:', error);
        this.$fun.alert('Ocurrió un error en la sincronización.', 'error');
      } finally {
        this.isSyncing = false;
      }
    },
    applyLocalFilters(filters) {
      const { sku } = filters;
      // Los filtros de Año, Mes, Proveedor, Rubro, Comprador y Estado ya fueron 
      // aplicados NATIVAMENTE por el Procedimiento Almacenado en SQL Server.
      // Por lo tanto, el backend nos devuelve exactamente lo que necesitamos.
      this.filteredDataset = this.masterDataset;
      
      if (sku && sku !== 'ALL') this.selectedSku = sku;
    },
    onTabChanged(tab) {
      this.activeTab = tab;
      if (tab === 'gestion' && this.currentFilters) {
        this.fetchPipelineStats(this.currentFilters);
      }
    },
    async fetchPipelineStats(filters) {
       let dateBegin = "20000101";
       let dateEnd = "20991231";
       if (filters && filters.year && filters.year !== 'ALL') {
          const years = filters.year.split(',').map(y => parseInt(y, 10)).filter(y => !isNaN(y));
          if (years.length > 0) {
             const minYear = Math.min(...years);
             const maxYear = Math.max(...years);
             if (filters.month && filters.month !== 'ALL') {
                const months = filters.month.split(',').map(m => parseInt(m, 10)).filter(m => !isNaN(m));
                if (months.length > 0) {
                   const minMonth = Math.min(...months);
                   const maxMonth = Math.max(...months);
                   dateBegin = `${minYear}${String(minMonth).padStart(2, '0')}01`;
                   const days = new Date(maxYear, maxMonth, 0).getDate();
                   dateEnd = `${maxYear}${String(maxMonth).padStart(2, '0')}${days}`;
                }
             } else {
                dateBegin = `${minYear}0101`;
                dateEnd = `${maxYear}1231`;
             }
          }
       }
       let payloadAreaID = null;
       if (this.$fun.isLevelArea()) {
          payloadAreaID = this.$fun.getUserInfo()?.CtrContract?.AreID ?? null;
       }
       const payload = {
         DateBegin: dateBegin,
         DateEnd: dateEnd,
         Years: filters && filters.year !== 'ALL' ? filters.year : null,
         Months: filters && filters.month !== 'ALL' ? filters.month : null,
         OrderType: filters && filters.OrderType !== 'ALL' ? filters.OrderType : null,
         SupCode: filters && filters.SupCode !== 'ALL' ? filters.SupCode : null,
         CategoryCode: filters && filters.CategoryCode !== 'ALL' ? filters.CategoryCode : null,
         ShopperID: filters && filters.ShopperID !== 'ALL' ? filters.ShopperID : null,
         OrdStatus: filters && filters.OrdStatus !== 'ALL' ? filters.OrdStatus : null,
         AreID: payloadAreaID
       };
       try {
         const statsResponse = await DashboardService.getPipelineStats(payload);
         this.pipelineStats = statsResponse.data || null;
       } catch (e) {
         console.error("Error obteniendo pipeline stats", e);
       }
    },
    async exportToExcel() {
      let dataToExport = this.filteredDataset;
      if (this.activeTab === 'historico' && this.selectedSku) {
        dataToExport = this.filteredDataset.filter(r => r.ItemCode === this.selectedSku);
      }
      if (!dataToExport || dataToExport.length === 0) {
        this.showSwal({ title: 'Sin Datos', text: 'No hay registros en el periodo actual para exportar.', icon: 'warning', confirmButtonText: 'Aceptar' });
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Datos');

      worksheet.columns = [
        { header: 'N° OC SUN EXPERT', key: 'OrdDocumentNumber', width: 18 },
        { header: 'N° PEDIDO SAP', key: 'DocNumSap', width: 16 },
        { header: 'FECHA OC', key: 'OrdDate', width: 15 },
        { header: 'PROVEEDOR', key: 'SupName', width: 40 },
        { header: 'RUC', key: 'SupCode', width: 15 },
        { header: 'RUBRO/CATEGORÍA', key: 'CategoryName', width: 22 },
        { header: 'CÓDIGO MATERIAL', key: 'ItemCode', width: 18 },
        { header: 'DESCRIPCIÓN', key: 'ItemDescription', width: 45 },
        { header: 'CANT. SOLICITADA', key: 'Quantity', width: 18 },
        { header: 'CANT. RECIBIDA', key: 'ReceivedQuantity', width: 18 },
        { header: 'PRECIO UNITARIO (USD)', key: 'UnitPrice', width: 22 },
        { header: 'DESCUENTO LÍNEA (%)', key: 'OddDiscountPercentage', width: 20 },
        { header: 'DESCUENTO LÍNEA (USD)', key: 'OddDiscountUsd', width: 22 },
        { header: 'DESCUENTO ORDEN (%)', key: 'OrdDiscountPercentage', width: 20 },
        { header: 'PRECIO TOTAL (USD)', key: 'TotalAmount', width: 20 },
        { header: 'ESTADO', key: 'OrdStatusName', width: 15 }
      ];

      worksheet.getRow(1).eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0A5C36' } 
        };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' }
        };
      });

      dataToExport.forEach(row => {
        worksheet.addRow({
          OrdDocumentNumber: row.OrdDocumentNumber || '',
          DocNumSap: row.DocNumSap || '',
          OrdDate: row.OrdDate || '',
          SupName: row.SupName || '',
          SupCode: row.SupCode || '',
          CategoryName: row.CategoryName || '',
          ItemCode: row.ItemCode || '',
          ItemDescription: row.ItemDescription || '',
          Quantity: row.Quantity || 0,
          ReceivedQuantity: row.ReceivedQuantity || 0,
          UnitPrice: row.UnitPrice || 0,
          OddDiscountPercentage: row.OddDiscountPercentage || 0,
          OddDiscountUsd: row.OddDiscountUsd || 0,
          OrdDiscountPercentage: row.OrdDiscountPercentage || 0,
          TotalAmount: row.TotalAmount || 0,
          OrdStatusName: row.OrdStatusName || ''
        });
      });

      worksheet.getColumn('UnitPrice').numFmt = '"$"#,##0.0000';
      worksheet.getColumn('OddDiscountPercentage').numFmt = '0.00"%"';
      worksheet.getColumn('OddDiscountUsd').numFmt = '"$"#,##0.00';
      worksheet.getColumn('OrdDiscountPercentage').numFmt = '0.00"%"';
      worksheet.getColumn('TotalAmount').numFmt = '"$"#,##0.00';
      worksheet.getColumn('Quantity').numFmt = '#,##0.00';
      worksheet.getColumn('ReceivedQuantity').numFmt = '#,##0.00';

      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: worksheet.columns.length }
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      
      const fileName = this.activeTab === 'historico' 
        ? `Historial_Precios_${this.selectedSku}_${new Date().toISOString().substring(0,10)}.xlsx`
        : `Resumen_Compras_${new Date().toISOString().substring(0,10)}.xlsx`;

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      this.showSwal({ title: 'Excel Descargado', text: 'Se descargó el detalle del período actual.', icon: 'success', confirmButtonText: 'Genial' });
    },
    async runAiAudit() {
      if (this.activeTab !== 'historico') {
        this.showSwal({ title: 'Cambio Requerido', text: 'La auditoría IA funciona analizando un material específico. Cambiando a Histórico...', icon: 'info' });
        this.activeTab = 'historico';
        return;
      }
      
      if (!this.selectedSku) {
        this.showSwal({ title: 'Material no seleccionado', text: 'Por favor, selecciona un material en la vista de Histórico para auditarlo.', icon: 'warning' });
        return;
      }

      const matData = this.filteredDataset.filter(r => r.ItemCode === this.selectedSku);
      if (matData.length === 0) {
        this.showSwal({ title: 'Sin Datos', text: 'No hay historial de compras para este material.', icon: 'warning' });
        return;
      }

      // 1. Recopilar contexto para IA
      const currentPrice = matData[matData.length - 1].UnitPrice || 0;
      const minPrice = Math.min(...matData.map(x => x.UnitPrice || 0));
      const maxPrice = Math.max(...matData.map(x => x.UnitPrice || 0));
      const avgPrice = matData.reduce((acc, x) => acc + (x.UnitPrice || 0), 0) / matData.length;
      const totalQty = matData.reduce((acc, x) => acc + (x.Quantity || 0), 0);
      const nombreMat = matData[0].ItemDescription;

      const provMap = {};
      matData.forEach(r => {
        const p = r.SupName || 'Desconocido';
        if(!provMap[p]) provMap[p] = { sum:0, count:0 };
        provMap[p].sum += (r.UnitPrice || 0);
        provMap[p].count++;
      });
      const provText = Object.entries(provMap).map(([p, data]) => {
        const avg = data.sum / data.count;
        return `${p} (Promedio: USD ${avg.toFixed(3)})`;
      }).join(', ');

      const promptContext = {
        sku: this.selectedSku,
        nombre: nombreMat,
        ultimoPrecio: currentPrice,
        promedioPrecio: avgPrice,
        minimoPrecio: minPrice,
        maximoPrecio: maxPrice,
        totalCantidadAdquirida: totalQty,
        proveedoresYPrecios: provText
      };

      const userQuery = `Audita el histórico para el material de packaging: ${JSON.stringify(promptContext)}`;
      const systemPrompt = `Actúas como Lead Packaging Buyer de Supply Chain para Sunshine. Analiza los precios, las desviaciones de costos y la matriz de proveedores reales para el periodo filtrado. Entrega un informe en español ultra-ejecutivo. Inicia con un breve resumen en un párrafo y luego haz una lista (con etiquetas <ul> y <li>) con exactamente 3 recomendaciones rápidas de negociación basadas en los precios reales suministrados. Usa <br> o <p> para separar ideas. Resalta nombres de proveedores y ahorros clave usando la clase HTML <strong class="ai-highlight">texto</strong> en formato HTML estándar sin formato markdown.`;

      // 2. Mostrar contenedor de carga
      this.aiAuditVisible = true;
      this.aiAuditLoading = true;
      this.aiAuditHtml = '';
      this.rawAiText = '';

      // KEY LOCAL O POR ENTORNO (.env)
      const GEMINI_API_KEY = process.env.VUE_APP_GEMINI_API_KEY ||'';
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-3-flash-preview:generateContent?key=${GEMINI_API_KEY}`;

      let success = false;
      let responseData = null;

      if (GEMINI_API_KEY) {
        let delay = 1000;
        for (let i = 0; i < 3; i++) {
          try {
            const response = await fetch(apiUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                contents: [{ parts: [{ text: userQuery }] }],
                systemInstruction: { parts: [{ text: systemPrompt }] }
              })
            });
            if (response.ok) {
              responseData = await response.json();
              success = true;
              break;
            } else {
              console.warn(`Intento ${i + 1} fallido con status: ${response.status}`);
            }
          } catch (e) { 
            console.error(`Error de red en intento ${i + 1}:`, e); 
          }
          if (!success && i < 2) {
            await new Promise(r => setTimeout(r, delay));
            delay *= 2;
          }
        }
      }

      // 3. Resultado de la auditoría
      this.aiAuditLoading = false;
      
      if (success && responseData?.candidates?.[0]?.content?.parts?.[0]?.text) {
        let text = responseData.candidates[0].content.parts[0].text;
        this.rawAiText = text.replace(/<[^>]*>?/gm, '').replace(/```html/gi, '').replace(/```/g, '').trim(); // Limpiar HTML para el copy
        let finalHtml = text.replace(/```html/gi, '').replace(/```/g, '').trim();
        this.aiAuditHtml = finalHtml;
      } else {
        // Fallback: Modo Heurístico Local
        this.rawAiText = `Diagnóstico Local: El material ha fluctuado entre USD ${minPrice.toFixed(3)} y USD ${maxPrice.toFixed(3)}. Proveedores: ${provText}. Recomendación: Concentrar demanda anual.`;
        this.aiAuditHtml = `
            <p style="color:#D97706; font-weight:bold; margin-bottom:10px;">
              <i class="fa-solid fa-triangle-exclamation"></i> Diagnóstico Local de Variación de Costos (API Key no configurada o fallida)
            </p>
            <p>El material <strong class="ai-highlight">${nombreMat} (${this.selectedSku})</strong> ha fluctuado entre un mínimo de <strong class="ai-highlight">USD ${minPrice.toFixed(3)}</strong> y un máximo de <strong class="ai-highlight">USD ${maxPrice.toFixed(3)}</strong>.</p>
            <p style="margin-top:8px;">Los proveedores clave que cotizan este material son <span style="font-style:italic; font-weight:600;">${provText}</span>.</p>
            <p style="margin-top:8px;"><strong>Recomendación:</strong> Se sugiere concentrar el 70% de la demanda anual con el proveedor de menor coste histórico para asegurar tarifas estables de embalaje.</p>
        `;
      }
    },
    async copyAiResponse() {
      if (!this.rawAiText) return;
      try {
        await navigator.clipboard.writeText(this.rawAiText);
        this.showSwal({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          icon: 'success',
          title: 'Texto copiado al portapapeles'
        });
      } catch (err) {
        console.error('No se pudo copiar el texto', err);
      }
    },
    showSwal(options) {
      const darkOptions = this.isDark ? {
        background: '#1E293B',
        color: '#F1F5F9'
      } : {};
      swal.fire({ ...options, ...darkOptions });
    }
  },
  computed: {
    isDark() {
      if (this.themeMode === 'dark')   return true;
      if (this.themeMode === 'light')  return false;
      return this.systemPrefersDark; // 'system'
    },
  },
};
</script>

<style>
/* ─── RESET SUAVE PARA EL MÓDULO ────────────────────────────────────────────── */
#packaging-dashboard {
  --pd-bg:          #F8FAFC;
  --pd-sidebar-bg:  #F1F5F9;
  --pd-card-bg:     #FFFFFF;
  --pd-border:      #E2E8F0;
  --pd-green:       #0A5C36;
  --pd-green-hover: #074226;
  --pd-yellow:      #F59E0B;
  --pd-white:       #FFFFFF;
  --pd-text:        #1E293B;
  --pd-text-muted:  #64748B;
  --pd-text-xs:     #94A3B8;
  --pd-radius-sm:   8px;
  --pd-radius-md:   12px;
  --pd-radius-lg:   16px;
  --pd-shadow-sm:   0 1px 3px rgba(0,0,0,.08);
  --pd-shadow-md:   0 4px 12px rgba(0,0,0,.10);
  --pd-sidebar-w:   256px;

  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: var(--pd-bg);
  position: relative;
  font-family: 'Inter', system-ui, -apple-system, sans-serif !important;
  font-size: 14px;
  color: var(--pd-text);
  box-sizing: border-box;
}
#packaging-dashboard *, #packaging-dashboard *::before, #packaging-dashboard *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
#packaging-dashboard h1, #packaging-dashboard h2, #packaging-dashboard h3,
#packaging-dashboard h4, #packaging-dashboard h5, #packaging-dashboard h6,
#packaging-dashboard p { margin: 0; padding: 0; }

/* ─── OVERRIDES QUIRÚRGICOS DE VUETIFY ───────────────────────────────────────── */
/* Vuetify puede resetear letter-spacing/text-transform en elementos dentro de v-main */
#packaging-dashboard button,
#packaging-dashboard button:hover,
#packaging-dashboard button:focus {
  text-transform: none !important;
  letter-spacing: normal !important;
  outline: none !important;
}
/* Quitar el outline de foco de Vuetify en selects */
#packaging-dashboard select:focus {
  outline: none !important;
}

/* ─── SCROLLBAR ─────────────────────────────────────────────────────────────── */
#packaging-dashboard ::-webkit-scrollbar       { width: 5px; height: 5px; }
#packaging-dashboard ::-webkit-scrollbar-track  { background: transparent; }
#packaging-dashboard ::-webkit-scrollbar-thumb  { background: #CBD5E1; border-radius: 4px; }

/* ─── SIDEBAR ────────────────────────────────────────────────────────────────── */
#packaging-dashboard .pd-sidebar {
  width: var(--pd-sidebar-w);
  min-width: var(--pd-sidebar-w);
  background: var(--pd-sidebar-bg);
  border-right: 1px solid var(--pd-border);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  flex-shrink: 0;
  z-index: 50;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s ease;
}

/* ─── MINI SIDEBAR (MODO CERRADO EN DESKTOP) ─── */
#packaging-dashboard .pd-sidebar--desktop-closed {
  width: 76px !important;
  min-width: 76px !important;
  overflow: visible !important;
}

/* Ocultar textos y filtros complejos en modo mini */
#packaging-dashboard .pd-sidebar--desktop-closed .pd-sb__brand > div:nth-child(2),
#packaging-dashboard .pd-sidebar--desktop-closed .pd-sb__section-label,
#packaging-dashboard .pd-sidebar--desktop-closed .pd-sb__filter,
#packaging-dashboard .pd-sidebar--desktop-closed .pd-sb__nav-btn span,
#packaging-dashboard .pd-sidebar--desktop-closed .pd-user-info,
#packaging-dashboard .pd-sidebar--desktop-closed .pd-user-more,
#packaging-dashboard .pd-sidebar--desktop-closed .pd-sb__update-time {
  display: none !important;
}

/* Centrar elementos en modo mini */
#packaging-dashboard .pd-sidebar--desktop-closed .pd-sb__brand {
  padding: 18px 0 !important;
  justify-content: center !important;
}

#packaging-dashboard .pd-sidebar--desktop-closed .pd-sb__nav-btn {
  justify-content: center !important;
  padding: 14px 0 !important;
}

#packaging-dashboard .pd-sidebar--desktop-closed .pd-sb__nav-btn i {
  font-size: 20px !important;
  margin: 0 !important;
  width: auto !important;
}

#packaging-dashboard .pd-sidebar--desktop-closed .pd-user-profile {
  justify-content: center !important;
  padding: 8px 0 !important;
}

#packaging-dashboard .pd-sidebar--desktop-closed .pd-sb__footer {
  padding: 12px 0 !important;
  border-top: 1px solid var(--pd-border) !important;
}

/* ─── BOTON FLOTANTE DE COLAPSO (MODERNO) ─── */
#packaging-dashboard .pd-sidebar-toggle {
  position: absolute;
  top: 32px; /* Alineado con la cabecera */
  left: calc(var(--pd-sidebar-w) - 12px);
  width: 24px;
  height: 24px;
  background: var(--pd-bg);
  border: 1px solid var(--pd-border);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 60;
  color: var(--pd-text-muted);
  box-shadow: 0 2px 4px rgba(0,0,0,0.06);
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s ease, background 0.2s ease;
}

#packaging-dashboard .pd-sidebar-toggle i {
  font-size: 10px;
  transition: transform 0.3s ease;
}

#packaging-dashboard .pd-sidebar-toggle:hover {
  background: var(--pd-table-th-bg, #F1F5F9);
  color: var(--pd-green);
  transform: scale(1.1);
}

#packaging-dashboard .pd-sidebar-toggle--closed {
  left: calc(76px - 12px);
}

#packaging-dashboard .pd-sidebar-toggle--closed i {
  transform: rotate(180deg);
}

/* ─── MAIN ───────────────────────────────────────────────────────────────────── */
#packaging-dashboard .pd-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  min-width: 0;
}
#packaging-dashboard .pd-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}
#packaging-dashboard .pd-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 40;
}

/* ─── RESPONSIVE ─────────────────────────────────────────────────────────────── */
@media (max-width: 1023px) {
  #packaging-dashboard .pd-sidebar {
    position: fixed;
    top: 0; left: 0;
    height: 100%;
    transform: translateX(-100%);
  }
  #packaging-dashboard .pd-sidebar.pd-sidebar--mobile-open {
    transform: translateX(0);
  }
}

/* ══════════════════════════════════════════════════════════════════════════════
   MODO OSCURO — todos los overrides con #packaging-dashboard.pd--dark
   para que tengan especificidad (1 ID + 1 class) > scoped (1 attr + 1 class)
══════════════════════════════════════════════════════════════════════════════ */
#packaging-dashboard.pd--dark {
  --pd-bg:          #0F172A;
  --pd-sidebar-bg:  #1E293B;
  --pd-card-bg:     #1E293B;
  --pd-border:      #334155;
  --pd-text:        #F1F5F9;
  --pd-text-muted:  #94A3B8;
  
  background-color: var(--pd-bg) !important;
  color: var(--pd-text) !important;
}

/* Sidebar dark */
#packaging-dashboard.pd--dark .pd-sb              { background: #1E293B !important; border-right-color: #334155 !important; }
#packaging-dashboard.pd--dark .pd-sb__brand       { border-bottom-color: #334155 !important; }
#packaging-dashboard.pd--dark .pd-sb__brand-title { color: #34D399 !important; }
#packaging-dashboard.pd--dark .pd-sb__brand-sub   { color: #64748B !important; }
#packaging-dashboard.pd--dark .pd-sb__section     { }
#packaging-dashboard.pd--dark .pd-sb__section--border { border-top-color: #334155 !important; }
#packaging-dashboard.pd--dark .pd-sb__section-label  { color: #475569 !important; }
#packaging-dashboard.pd--dark .pd-sb__nav-btn          { color: #94A3B8 !important; background: transparent !important; }
#packaging-dashboard.pd--dark .pd-sb__nav-btn:hover    { background: rgba(255,255,255,.06) !important; color: #F1F5F9 !important; }
#packaging-dashboard.pd--dark .pd-sb__nav-btn--active  { background: #0A5C36 !important; color: #fff !important; }
#packaging-dashboard.pd--dark .pd-sidebar-toggle       { background: #1E293B !important; border-color: #334155 !important; color: #94A3B8 !important; }
#packaging-dashboard.pd--dark .pd-sidebar-toggle:hover { background: #334155 !important; color: #34D399 !important; }
#packaging-dashboard.pd--dark .pd-sb__filter-label     { color: #475569 !important; }
#packaging-dashboard.pd--dark .pd-sb__select           { background: #263548 !important; border-color: #334155 !important; color: #E2E8F0 !important; }
#packaging-dashboard.pd--dark .pd-sb__footer           { background: transparent !important; border-top-color: #334155 !important; }
#packaging-dashboard.pd--dark .pd-user-profile         { background: transparent !important; border: none !important; }
#packaging-dashboard.pd--dark .pd-user-profile:hover   { background: rgba(255,255,255,0.06) !important; }
#packaging-dashboard.pd--dark .pd-user-avatar          { background: #334155 !important; border: none !important; }
#packaging-dashboard.pd--dark .pd-user-initials        { color: #F1F5F9 !important; }
#packaging-dashboard.pd--dark .pd-user-name            { color: #E2E8F0 !important; }
#packaging-dashboard.pd--dark .pd-user-role            { color: #94A3B8 !important; }
#packaging-dashboard.pd--dark .pd-sb__close-btn        { color: #64748B !important; }

/* Header dark */
#packaging-dashboard.pd--dark .pd-hdr             { background: #1E293B !important; border-bottom-color: #334155 !important; }
#packaging-dashboard.pd--dark .pd-hdr__title      { color: #F1F5F9 !important; }
#packaging-dashboard.pd--dark .pd-hdr__subtitle   { color: #64748B !important; }
#packaging-dashboard.pd--dark .pd-hdr__hamburger  { color: #94A3B8 !important; }
#packaging-dashboard.pd--dark .pd-hdr__btn--outline { background: #263548 !important; border-color: #334155 !important; color: #CBD5E1 !important; }
#packaging-dashboard.pd--dark .pd-hdr__btn--outline:hover { background: #334155 !important; }

/* Contenido: Banner */
#packaging-dashboard.pd--dark .pds__banner { background: #064E3B !important; border-color: #065F46 !important; color: #A7F3D0 !important; }

/* KPI cards dark */
#packaging-dashboard.pd--dark .pds__kpi            { background: #1E293B !important; border-color: #334155 !important; }
#packaging-dashboard.pd--dark .pds__kpi-value      { color: #F1F5F9 !important; }
#packaging-dashboard.pd--dark .pds__kpi-label,
#packaging-dashboard.pd--dark .pds__kpi-sub        { color: #475569 !important; }

/* Chart cards dark */
#packaging-dashboard.pd--dark .pds__chart-card     { background: #1E293B !important; border-color: #334155 !important; }
#packaging-dashboard.pd--dark .pds__card-head h3   { color: #E2E8F0 !important; }
#packaging-dashboard.pd--dark .pds__card-head p    { color: #475569 !important; }
#packaging-dashboard.pd--dark .pds__legend-label   { color: #E2E8F0 !important; }
#packaging-dashboard.pd--dark .pds__legend-sub     { color: #94A3B8 !important; }

/* Aging dark */
#packaging-dashboard.pd--dark .pds__aging-cell     { background: #263548 !important; border-color: #334155 !important; }
#packaging-dashboard.pd--dark .pds__aging-count    { color: #F1F5F9 !important; }
#packaging-dashboard.pd--dark .pds__aging-label,
#packaging-dashboard.pd--dark .pds__aging-pct,
#packaging-dashboard.pd--dark .pds__aging-note     { color: #475569 !important; }

/* Balance dark */
#packaging-dashboard.pd--dark .pds__balance-card   { background: #1A2E2A !important; border-color: #0A5C36 !important; }
#packaging-dashboard.pd--dark .pds__balance-card-top span { color: #64748B !important; }
#packaging-dashboard.pd--dark .pds__balance-hint   { color: #475569 !important; }

/* Tabla dark */
#packaging-dashboard.pd--dark .pds__table-wrap     { background: #1E293B !important; border-color: #334155 !important; }
#packaging-dashboard.pd--dark .pds__table-head     { background: #263548 !important; border-bottom-color: #334155 !important; }
#packaging-dashboard.pd--dark .pds__table-head h3  { color: #E2E8F0 !important; }
#packaging-dashboard.pd--dark .pds__table-head p,
#packaging-dashboard.pd--dark .pds__table-head span { color: #64748B !important; }
#packaging-dashboard.pd--dark .pds__table thead tr { background: #263548 !important; }
#packaging-dashboard.pd--dark .pds__table th       { color: #64748B !important; }
#packaging-dashboard.pd--dark .pds__table td       { color: #CBD5E1 !important; border-bottom-color: #263548 !important; }
#packaging-dashboard.pd--dark .pds__table tbody tr:hover { background: #263548 !important; }
#packaging-dashboard.pd--dark .pds__badge-oc       { background: #334155 !important; border-color: #475569 !important; color: #CBD5E1 !important; }

/* Paginación dark */
#packaging-dashboard.pd--dark .pds__pagination     { background: #263548 !important; border-top-color: #334155 !important; }
#packaging-dashboard.pd--dark .pds__pagination button { background: #334155 !important; border-color: #475569 !important; color: #CBD5E1 !important; }
#packaging-dashboard.pd--dark .pds__pagination button:hover:not(:disabled) { background: #475569 !important; }
#packaging-dashboard.pd--dark .pds__pagination span { color: #64748B !important; }

/* Contenido principal dark */
#packaging-dashboard.pd--dark .pd-content { background: #0F172A !important; }

/* Theme toggle dark */
#packaging-dashboard.pd--dark .pd-hdr__theme-group { background: #334155 !important; }
#packaging-dashboard.pd--dark .pd-hdr__theme-btn   { color: #64748B !important; }
#packaging-dashboard.pd--dark .pd-hdr__theme-btn:hover { background: #475569 !important; color: #CBD5E1 !important; }
#packaging-dashboard.pd--dark .pd-hdr__theme-btn--active { background: #1E293B !important; color: #34D399 !important; }

/* DashboardSelect dark */
#packaging-dashboard.pd--dark .pd-select__control { background: #263548 !important; border-color: #334155 !important; color: #E2E8F0 !important; }
#packaging-dashboard.pd--dark .pd-select--open .pd-select__control { border-color: #34D399 !important; box-shadow: 0 0 0 3px rgba(52,211,153,0.1) !important; }
#packaging-dashboard.pd--dark .pd-select:hover .pd-select__control { border-color: #475569 !important; }
#packaging-dashboard.pd--dark .pd-select__arrow { color: #64748B !important; }
#packaging-dashboard.pd--dark .pd-select--open .pd-select__arrow { color: #34D399 !important; }
#packaging-dashboard.pd--dark .pd-select__menu { background: #1E293B !important; border-color: #334155 !important; box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important; }
#packaging-dashboard.pd--dark .pd-select__search { border-bottom-color: #334155 !important; color: #64748B !important; }
#packaging-dashboard.pd--dark .pd-select__search input { color: #F1F5F9 !important; }
#packaging-dashboard.pd--dark .pd-select__search input::placeholder { color: #64748B !important; }
#packaging-dashboard.pd--dark .pd-select__empty { color: #64748B !important; }
#packaging-dashboard.pd--dark .pd-select__option { color: #CBD5E1 !important; }
#packaging-dashboard.pd--dark .pd-select__option:hover { background: #263548 !important; color: #F1F5F9 !important; }
#packaging-dashboard.pd--dark .pd-select__option--selected { background: #064E3B !important; color: #34D399 !important; }



/* DashboardHistory dark */
#packaging-dashboard.pd--dark .pdh__top-section       { background: #1E293B !important; border-color: #334155 !important; }
#packaging-dashboard.pd--dark .pdh__header            { border-bottom-color: #334155 !important; }
#packaging-dashboard.pd--dark .pdh__title-box h2      { color: #F1F5F9 !important; }
#packaging-dashboard.pd--dark .pdh__title-box p       { color: #94A3B8 !important; }
#packaging-dashboard.pd--dark .pdh__select-wrap label { color: #64748B !important; }
#packaging-dashboard.pd--dark .pdh__select-wrap select { background: #263548 !important; border-color: #334155 !important; color: #34D399 !important; }

#packaging-dashboard.pd--dark .pdh__kpi-card          { background: #263548 !important; border-color: #334155 !important; }
#packaging-dashboard.pd--dark .pdh__kpi-card--highlight { background: #1A2E2A !important; border-color: #0A5C36 !important; }
#packaging-dashboard.pd--dark .pdh__kpi-card span     { color: #64748B !important; }
#packaging-dashboard.pd--dark .pdh__kpi-card h3       { color: #F1F5F9 !important; }
#packaging-dashboard.pd--dark .pdh__kpi-card h4       { color: #34D399 !important; }
#packaging-dashboard.pd--dark .pdh__kpi-card p        { color: #94A3B8 !important; }
#packaging-dashboard.pd--dark .pdh__sku-code          { color: #64748B !important; }
#packaging-dashboard.pd--dark .pdh__empty             { color: #64748B !important; }

#packaging-dashboard.pd--dark .pd-receipt-header     { background: #1E293B !important; border-color: #334155 !important; }
#packaging-dashboard.pd--dark .pd-receipt-header h2  { color: #E2E8F0 !important; }
#packaging-dashboard.pd--dark .pd-receipt-header p   { color: #94A3B8 !important; }

#packaging-dashboard.pd--dark .pdh__table-section     { background: #1E293B !important; border-color: #334155 !important; }
#packaging-dashboard.pd--dark .pdh__table-head        { background: #263548 !important; border-bottom-color: #334155 !important; }
#packaging-dashboard.pd--dark .pdh__table-head h3     { color: #E2E8F0 !important; }
#packaging-dashboard.pd--dark .pdh__table-head p      { color: #94A3B8 !important; }

#packaging-dashboard.pd--dark .pdh__table-wrap th     { background: rgba(0,0,0,0.2) !important; color: #64748B !important; border-bottom-color: #334155 !important; }
#packaging-dashboard.pd--dark .pdh__table-wrap td     { color: #CBD5E1 !important; border-bottom-color: #334155 !important; }
#packaging-dashboard.pd--dark .pdh__table-wrap tr:hover { background: rgba(255,255,255,0.02) !important; }

/* ─── CONTENEDOR IA ───────────────────────────────────────────────────────── */
.pd-ai-container {
  background: var(--pd-white);
  border: 1px solid #E2E8F0;
  padding: 24px !important;
  box-sizing: border-box !important;
  border-radius: var(--pd-radius-lg);
  box-shadow: var(--pd-shadow-sm);
  margin-bottom: 32px !important; /* Separación forzada con la alerta de Foco Temporal */
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.pd-ai-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.pd-ai-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--pd-green);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 8px;
}

.pd-ai-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.pd-ai-btn {
  background: none;
  border: none;
  color: var(--pd-text-muted);
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s;
  padding: 6px 10px;
  border-radius: 6px;
}

.pd-ai-btn:hover {
  color: var(--pd-green);
  background: rgba(10, 92, 54, 0.05);
}

.pd-ai-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 0;
  color: var(--pd-text-muted);
  font-size: 13px;
}
.pd-ai-loading i {
  color: var(--pd-green);
  font-size: 16px;
}

.pd-ai-body {
  font-size: 13.5px;
  color: var(--pd-text);
  line-height: 1.7;
}

.pd-ai-body p {
  margin-bottom: 14px;
}
.pd-ai-body strong, .pd-ai-body b {
  font-weight: 700;
  color: var(--pd-text);
}
.pd-ai-body ul, .pd-ai-body ol {
  margin-left: 20px;
  margin-bottom: 14px;
  margin-top: 8px;
}
.pd-ai-body li {
  margin-bottom: 8px;
  padding-left: 4px;
}
.pd-ai-body li::marker {
  color: var(--pd-green);
}

/* Highlight de la IA */
.ai-highlight {
  color: var(--pd-green);
  font-weight: bold;
}

/* Modo oscuro IA */
#packaging-dashboard.pd--dark .pd-ai-container {
  background: #263548 !important; 
  border-color: #334155 !important;
}
#packaging-dashboard.pd--dark .pd-ai-title {
  color: #34D399 !important; /* Verde claro legible en oscuro */
}
#packaging-dashboard.pd--dark .pd-ai-loading {
  color: #94A3B8 !important;
}
#packaging-dashboard.pd--dark .pd-ai-loading i {
  color: #34D399 !important;
}
#packaging-dashboard.pd--dark .pd-ai-btn {
  color: #94A3B8;
}
#packaging-dashboard.pd--dark .pd-ai-btn:hover {
  color: #34D399;
  background: rgba(52, 211, 153, 0.1);
}
#packaging-dashboard.pd--dark .pd-ai-body {
  color: #CBD5E1;
}
#packaging-dashboard.pd--dark .pd-ai-body strong,
#packaging-dashboard.pd--dark .pd-ai-body b {
  color: #F1F5F9;
}
#packaging-dashboard.pd--dark .ai-highlight {
  color: #34D399 !important; /* Resalte verde claro en modo oscuro */
}
#packaging-dashboard.pd--dark .pd-ai-body li::marker {
  color: #34D399;
}

/* Buscador de Tablas (Global) */
.pd-table-search { position: relative; display: flex; align-items: center; max-width: 100%; }
.pd-table-search i { position: absolute; left: 14px !important; color: #94A3B8; font-size: 13px; pointer-events: none; z-index: 5; }
.pd-table-search input {
  padding: 8px 16px 8px 38px !important; font-size: 12px !important; border: 1px solid #E2E8F0 !important;
  border-radius: 8px !important; outline: none !important; width: 260px !important; max-width: 100% !important; color: #1E293B !important;
  background: #F8FAFC !important; transition: all 0.2s ease !important; font-family: inherit !important;
}
.pd-table-search input::placeholder { color: #94A3B8 !important; }
.pd-table-search input:hover { background: #fff !important; border-color: #CBD5E1 !important; }
.pd-table-search input:focus { background: #fff !important; border-color: #058c99 !important; box-shadow: 0 0 0 3px rgba(5,140,153,0.15) !important; }

#packaging-dashboard.pd--dark .pd-table-search input { background: #0F172A !important; border-color: #334155 !important; color: #F1F5F9 !important; }
#packaging-dashboard.pd--dark .pd-table-search input:hover { background: #1E293B !important; border-color: #475569 !important; }
#packaging-dashboard.pd--dark .pd-table-search input:focus { background: #1E293B !important; border-color: #34D399 !important; box-shadow: 0 0 0 3px rgba(52,211,153,0.15) !important; }
#packaging-dashboard.pd--dark .pd-table-search input::placeholder { color: #64748B !important; }
#packaging-dashboard.pd--dark .pd-table-search i { color: #64748B !important; }

</style>
